import {
  isAvailabilityCheckBlock,
  isEpilotCatalogProductsBlock
} from '@epilot/journey-logic-commons/src/next/journey'
import {
  isBlockFact,
  isBlockReference
} from '@epilot/journey-logic-commons/src/next/references'
import type { ProductAvailabilityUserFiltersValue } from '@epilot/journey-logic-commons/src/services/pricing-service'
import { productAvailabilityCheck } from '@epilot/journey-logic-commons/src/services/pricing-service'
import { AVAILABILITY_CHECK_FUNCTIONS } from '@epilot/journey-logic-commons/src/types/next'

import { CustomFunction } from '../operators/customFunction'

/**
 * Responsible for cross-checking availability according to the Products selected by
 * upcoming Product select blocks.
 *
 * This is currently used by AvailabilityCheck blocks to jump to a "not available" step in
 * case of no matches.
 *
 * TODO: wire the AbortSignal to the API requests
 */
export const productAvailabilityCheckFunction = new CustomFunction(
  AVAILABILITY_CHECK_FUNCTIONS.PRODUCT_AVAILABILITY_CHECK,
  async (factValue, _comparisonValue, context, _args, _signal) => {
    if (!isBlockFact(context?.fact)) return true

    const availabilityBlock = context.blockGetter(context.fact.referenceId)

    if (!availabilityBlock) {
      throw new Error('Expected AvailabilityCheck block to be found')
    }

    if (!isAvailabilityCheckBlock(availabilityBlock)) {
      throw new Error('Expected an AvailabilityCheck block')
    }

    // extracts all product IDs from the references of the AvailabilityCheck block
    const productIds = Object.values(availabilityBlock.references).reduce<
      string[]
    >((acc, ref) => {
      if (isBlockReference(ref)) {
        const block = context.blockGetter(ref.referenceId)

        if (isEpilotCatalogProductsBlock(block)) {
          acc.push(...block.settings.products.map((p) => p.productId))
        }
      }

      return acc
    }, [])

    const availableProducts = await productAvailabilityCheck(
      productIds,
      factValue as ProductAvailabilityUserFiltersValue
    )

    return availableProducts.length === 0
  },
  // this check is known for being slow, therefore we need a longer timeout
  30_000
)
